import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { useToast } from '../contexts/ToastContext.jsx';
import { useNotifications } from '../contexts/NotificationContext.jsx';
import { ApiClient } from '../utils/apiClient.js';
import LoadingSpinner from '../components/LoadingSpinner.jsx';

/**
 * UserNotifications component for viewing user notifications
 * Shows only active notifications created after user's account creation date
 * Implements theme-aware styling and follows existing patterns
 */
const UserNotifications = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const { markAsRead } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Initialize API client
  const apiClient = new ApiClient(getToken);

  // Load notifications on component mount
  useEffect(() => {
    loadNotifications();
  }, []);

  /**
   * Load user notifications from API with enhanced error handling
   */
  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/api/notifications');
      
      if (response.success) {
        setNotifications(response.notifications || []);
      } else {
        // Handle structured error responses
        if (response.error && response.error.code === 'AUTH_ERROR') {
          setError('Session expired. Please refresh the page and try again.');
          showToast('Session expired. Please refresh the page and try again.', 'error');
        } else if (response.error && response.error.code === 'RESOURCE_NOT_FOUND') {
          setError('User account not found. Please contact support.');
          showToast('User account not found. Please contact support.', 'error');
        } else {
          throw new Error(response.error?.message || response.message || 'Failed to load notifications');
        }
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
        showToast('Network error. Please check your connection and try again.', 'error');
      } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
        setError('Session expired. Please refresh the page and try again.');
        showToast('Session expired. Please refresh the page and try again.', 'error');
      } else if (error.message.includes('500')) {
        setError('Server error. Please try again later.');
        showToast('Server error. Please try again later.', 'error');
      } else {
        setError(error.message || 'Failed to load notifications. Please try again.');
        showToast(error.message || 'Failed to load notifications', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle notification click to read and display full content with enhanced error handling
   */
  const handleNotificationClick = async (notification) => {
    try {
      // If already selected, close it
      if (selectedNotification && selectedNotification.id === notification.id) {
        setSelectedNotification(null);
        return;
      }

      // Mark notification as read if not already read
      if (!notification.is_read) {
        const result = await markAsRead(notification.id);
        
        if (result.success) {
          // Update local state to reflect read status
          setNotifications(prev => 
            prev.map(n => 
              n.id === notification.id 
                ? { ...n, is_read: true }
                : n
            )
          );
        } else {
          // Handle structured error responses
          if (result.error && result.error.code === 'AUTH_ERROR') {
            showToast('Session expired. Please refresh the page and try again.', 'error');
            return;
          } else if (result.error && result.error.code === 'RESOURCE_NOT_FOUND') {
            showToast('This notification is no longer available.', 'error');
            // Remove the notification from local state
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
            return;
          } else {
            throw new Error(result.error?.message || result.error || 'Failed to mark notification as read');
          }
        }
      }

      // Set selected notification for display
      setSelectedNotification({ ...notification, is_read: true });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showToast('Network error. Please check your connection and try again.', 'error');
      } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
        showToast('Session expired. Please refresh the page and try again.', 'error');
      } else if (error.message.includes('404')) {
        showToast('This notification is no longer available.', 'error');
        // Remove the notification from local state
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        return;
      } else {
        showToast(error.message || 'Failed to mark notification as read', 'error');
      }
      
      // Still show the notification even if marking as read failed
      setSelectedNotification(notification);
    }
  };

  /**
   * Handle notification deletion (hide for current user)
   */
  const handleDeleteNotification = (notification, event) => {
    event.stopPropagation(); // Prevent row click
    setDeleteConfirm(notification);
  };

  /**
   * Confirm and execute notification deletion
   */
  const confirmDeleteNotification = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      const response = await apiClient.delete(`/api/notifications/${deleteConfirm.id}`);
      
      if (response.success) {
        showToast('Notification deleted successfully', 'success');
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== deleteConfirm.id));
        // Clear selection if this notification was selected
        if (selectedNotification?.id === deleteConfirm.id) {
          setSelectedNotification(null);
        }
        setDeleteConfirm(null);
      } else {
        // Handle structured error responses
        if (response.error && response.error.code === 'AUTH_ERROR') {
          showToast('Session expired. Please refresh the page and try again.', 'error');
        } else if (response.error && response.error.code === 'RESOURCE_NOT_FOUND') {
          showToast('Notification not found. It may have already been deleted.', 'error');
          // Remove from local state anyway
          setNotifications(prev => prev.filter(n => n.id !== deleteConfirm.id));
          setDeleteConfirm(null);
        } else {
          throw new Error(response.error?.message || response.message || 'Failed to delete notification');
        }
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showToast('Network error. Please check your connection and try again.', 'error');
      } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
        showToast('Session expired. Please refresh the page and try again.', 'error');
      } else if (error.message.includes('403') || error.message.includes('forbidden')) {
        showToast('Access denied. Unable to delete notification.', 'error');
      } else if (error.message.includes('404')) {
        showToast('Notification not found. It may have already been deleted.', 'error');
        // Remove from local state anyway
        setNotifications(prev => prev.filter(n => n.id !== deleteConfirm.id));
        setDeleteConfirm(null);
      } else {
        showToast(error.message || 'Failed to delete notification', 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      {/* Page Header */}
      <div className="mb-6">
        <h2 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Notifications
        </h2>
        <p className={`mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          Stay updated with important announcements and information
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`mb-6 p-4 rounded-lg border ${
          theme === 'dark' 
            ? 'bg-red-900/20 border-red-500/30 text-red-400' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            <span className="text-lg">⚠️</span>
            <span>{error}</span>
          </div>
          <button
            onClick={loadNotifications}
            className="mt-2 text-sm underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <LoadingSpinner />
            <p className={`mt-4 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Loading notifications...
            </p>
          </div>
        </div>
      )}

      {/* Notifications Table */}
      {!loading && !error && (
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow border ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          {notifications.length === 0 ? (
            <div className="p-8 text-center">
              <span className="text-4xl mb-4 block">🔔</span>
              <h3 className={`text-lg font-medium mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                No notifications yet
              </h3>
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                You'll see important updates and announcements here when they're available.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Title
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Date
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Status
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'
                }`}>
                  {notifications.map((notification) => (
                    <tr
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`cursor-pointer transition-colors ${
                        theme === 'dark' 
                          ? 'hover:bg-gray-700' 
                          : 'hover:bg-gray-50'
                      } ${
                        selectedNotification && selectedNotification.id === notification.id
                          ? theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                          : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${
                          !notification.is_read 
                            ? `font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`
                            : theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {notification.title}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {formatDate(notification.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          notification.is_read
                            ? theme === 'dark'
                              ? 'bg-gray-700 text-gray-300'
                              : 'bg-gray-100 text-gray-800'
                            : theme === 'dark'
                              ? 'bg-teal-900/30 text-teal-400'
                              : 'bg-teal-100 text-teal-800'
                        }`}>
                          {notification.is_read ? 'Read' : 'Unread'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={(e) => handleDeleteNotification(notification, e)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors"
                          title="Delete notification"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Selected Notification Details */}
      {selectedNotification && (
        <div className={`mt-6 bg-white dark:bg-gray-800 rounded-lg shadow border p-6 ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-start justify-between mb-4">
            <h3 className={`text-xl font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {selectedNotification.title}
            </h3>
            <button
              onClick={() => setSelectedNotification(null)}
              className={`text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors`}
              aria-label="Close notification details"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className={`text-sm mb-4 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {formatDate(selectedNotification.created_at)}
          </div>
          
          <div className={`prose max-w-none ${
            theme === 'dark' ? 'prose-invert' : ''
          }`}>
            <div className={`${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {selectedNotification.body.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-3 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 border ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Delete Notification
            </h3>
            <p className={`mb-6 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Are you sure you want to delete "{deleteConfirm.title}"? This will hide it from your notifications and cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className={`px-4 py-2 transition-colors disabled:opacity-50 ${
                  theme === 'dark' 
                    ? 'text-gray-300 hover:text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteNotification}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 dark:text-white"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserNotifications;
